/**
 * ProfilePage Component
 * 
 * User profile management page with sections for:
 * - User info display with avatar and verification status
 * - Edit display name
 * - Change password
 * - Update avatar
 */

import { useState, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User, Mail, Shield, Calendar, CheckCircle2, AlertCircle, Save, Loader2, Eye, EyeOff, ArrowLeft, Upload, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUser, uploadAvatar, changePassword, getAccounts, setPassword, disconnectAccount, linkSocialAccount } from '../../api/auth';
import type { ConnectedAccount } from '../../api/auth';
import { format } from 'date-fns';
import { btn } from '../../components/settings/buttonStyles';
import { PasswordInput } from '../../components/ui/PasswordInput';

interface ProfilePageProps {
  /** When provided, shows a back-button header (standalone overlay mode).
   *  When omitted, the header is hidden (embedded in Settings modal). */
  onBack?: () => void;
}

export function ProfilePage({ onBack }: ProfilePageProps) {
  const { user, refetch } = useAuth();

  const { data: accountsData, refetch: refetchAccounts } = useQuery({
    queryKey: ['me-accounts'],
    queryFn: getAccounts,
  });
  const accounts: ConnectedAccount[] = accountsData?.accounts ?? [];
  const hasPassword = accounts.some((a) => a.provider === 'credential');

  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const handleAvatarError = useCallback(() => setAvatarFailed(true), []);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Loading and error states
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  // Get initials for avatar fallback
  const getInitials = () => {
    if (user.name) {
      const parts = user.name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  // Password validation
  const validatePassword = (password: string) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
    if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain a number';
    return null;
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return strength;
  };

  const handleUpdateName = async () => {
    if (!name.trim()) {
      setNameError('Name cannot be empty');
      return;
    }

    setNameError(null);
    setNameSuccess(false);
    setIsUpdatingName(true);

    try {
      await updateUser({ name: name.trim() });
      await refetch();
      setNameSuccess(true);
      setIsEditingName(false);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarUrl('');
    const objectUrl = URL.createObjectURL(file);
    setAvatarPreview(objectUrl);
  };

  const clearFileSelection = () => {
    setAvatarFile(null);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
      setAvatarPreview(null);
    }
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleRemoveAvatar = async () => {
    setAvatarError(null);
    setIsUpdatingAvatar(true);
    try {
      await updateUser({ image: null });
      await refetch();
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Failed to remove avatar');
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const handleUpdateAvatar = async () => {
    setAvatarError(null);
    setAvatarSuccess(false);
    setIsUpdatingAvatar(true);

    try {
      if (avatarFile) {
        // File upload path
        await uploadAvatar(avatarFile);
      } else if (avatarUrl.trim()) {
        // URL path
        try {
          new URL(avatarUrl);
        } catch {
          setAvatarError('Please enter a valid URL');
          setIsUpdatingAvatar(false);
          return;
        }
        await updateUser({ image: avatarUrl.trim() });
      } else {
        setAvatarError('Choose a file or enter a URL');
        setIsUpdatingAvatar(false);
        return;
      }

      await refetch();
      setAvatarSuccess(true);
      setIsEditingAvatar(false);
      setAvatarUrl('');
      clearFileSelection();
      setTimeout(() => setAvatarSuccess(false), 3000);
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Failed to update avatar');
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (hasPassword && !currentPassword.trim()) {
      setPasswordError('Current password is required');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordError(null);
    setPasswordSuccess(false);
    setIsChangingPassword(true);

    try {
      if (hasPassword) {
        await changePassword({ currentPassword, newPassword });
      } else {
        await setPassword(newPassword);
        await refetchAccounts();
      }
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : hasPassword ? 'Failed to change password' : 'Failed to set password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const passwordStrength = getPasswordStrength(newPassword);

  // When embedded in Settings, omit the outer scroll wrapper — the Settings
  // layout already handles scrolling. When standalone (onBack provided), keep
  // the full-height scrollable wrapper.
  const content = (
    <div className={onBack ? 'max-w-3xl mx-auto px-4 py-8' : 'space-y-6'}>
      {/* Header with Back Button — only in standalone overlay mode */}
      {onBack && (
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
            aria-label="Go back to dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
          </button>
          <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark">Profile</h1>
        </div>
      )}

        {/* User Info Card */}
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-sm border border-border-subtle-light dark:border-border-subtle-dark p-6 mb-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {user.avatarUrl && !avatarFailed ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name || user.email}
                  className="w-24 h-24 rounded-full object-cover ring-4 ring-border-subtle-light dark:ring-border-subtle-dark"
                  onError={handleAvatarError}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-2xl font-bold ring-4 ring-border-subtle-light dark:ring-border-subtle-dark">
                  {getInitials()}
                </div>
              )}
            </div>

            {/* User Details */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                {user.name || 'User'}
              </h2>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-text-secondary-light dark:text-text-secondary-dark">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{user.email}</span>
                  {user.emailVerified !== false ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" aria-label="Verified" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" aria-label="Unverified" />
                  )}
                </div>

                {user.subscriptionTier && (
                  <div className="flex items-center gap-2 text-text-secondary-light dark:text-text-secondary-dark">
                    <Shield className="w-4 h-4" />
                    <span className="capitalize">{user.subscriptionTier} Plan</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-text-secondary-light dark:text-text-secondary-dark">
                  <Calendar className="w-4 h-4" />
                  <span>Member since {formatDate(user.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Display Name */}
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-sm border border-border-subtle-light dark:border-border-subtle-dark p-6 mb-6">
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Display Name
          </h3>

          {!isEditingName ? (
            <div className="flex items-center justify-between">
              <span className="text-text-secondary-light dark:text-text-secondary-dark">{user.name || 'Not set'}</span>
              <button
                onClick={() => {
                  setName(user.name || '');
                  setIsEditingName(true);
                  setNameError(null);
                }}
                className={btn.ghost.md}
              >
                Edit
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-2 bg-surface-raised-light dark:bg-surface-base-dark border border-border-hover-light dark:border-border-hover-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              />
              
              {nameError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {nameError}
                </div>
              )}

              {nameSuccess && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  Name updated successfully
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleUpdateName}
                  disabled={isUpdatingName}
                  className={`${btn.primary.md} flex items-center gap-2`}
                >
                  {isUpdatingName ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setNameError(null);
                  }}
                  disabled={isUpdatingName}
                  className={btn.ghost.md}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Update Avatar */}
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-sm border border-border-subtle-light dark:border-border-subtle-dark p-6 mb-6">
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">Avatar</h3>

          {!isEditingAvatar ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-text-secondary-light dark:text-text-secondary-dark">
                  {user.avatarUrl ? 'Custom avatar set' : 'Using default avatar'}
                </span>
                <div className="flex gap-2">
                  {user.avatarUrl && (
                    <button
                      onClick={handleRemoveAvatar}
                      disabled={isUpdatingAvatar}
                      className={btn.danger.md}
                    >
                      {isUpdatingAvatar ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsEditingAvatar(true);
                      setAvatarError(null);
                    }}
                    className={btn.ghost.md}
                  >
                    Change
                  </button>
                </div>
              </div>
              {avatarError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {avatarError}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Hidden file input */}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleFileSelect}
              />

              {/* Upload button + preview */}
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-border-hover-light dark:border-border-hover-dark hover:border-primary-400 dark:hover:border-primary-500 rounded-lg text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  {avatarFile ? avatarFile.name : 'Choose image…'}
                </button>

                {avatarPreview && (
                  <div className="relative">
                    <img
                      src={avatarPreview}
                      alt="Preview"
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-primary-400"
                    />
                    <button
                      type="button"
                      onClick={clearFileSelection}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white"
                      aria-label="Remove selected file"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
              </div>

              <p className="text-xs text-text-tertiary-light dark:text-text-secondary-dark">
                JPG, PNG, GIF or WebP · max 5 MB
              </p>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border-subtle-light dark:bg-border-subtle-dark" />
                <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">or paste a URL</span>
                <div className="flex-1 h-px bg-border-subtle-light dark:bg-border-subtle-dark" />
              </div>

              <input
                type="url"
                value={avatarUrl}
                onChange={(e) => {
                  setAvatarUrl(e.target.value);
                  if (e.target.value) clearFileSelection();
                }}
                placeholder="https://example.com/avatar.jpg"
                className="w-full px-4 py-2 bg-surface-raised-light dark:bg-surface-base-dark border border-border-hover-light dark:border-border-hover-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              />

              {avatarError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {avatarError}
                </div>
              )}

              {avatarSuccess && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  Avatar updated successfully
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleUpdateAvatar}
                  disabled={isUpdatingAvatar || (!avatarFile && !avatarUrl.trim())}
                  className={`${btn.primary.md} flex items-center gap-2`}
                >
                  {isUpdatingAvatar ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditingAvatar(false);
                    setAvatarUrl('');
                    clearFileSelection();
                    setAvatarError(null);
                  }}
                  disabled={isUpdatingAvatar}
                  className={btn.ghost.md}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Password Section — smart: "Set password" for OAuth-only, "Change password" otherwise */}
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-sm border border-border-subtle-light dark:border-border-subtle-dark p-6">
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">
            {hasPassword ? 'Change Password' : 'Set Password'}
          </h3>

          {!hasPassword && (
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
              You haven&apos;t set a password yet. Adding one lets you sign in with email.
            </p>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            {/* Current Password — only shown when hasPassword */}
            {hasPassword && (
              <div>
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full px-4 py-2 pr-10 bg-surface-raised-light dark:bg-surface-base-dark border border-border-hover-light dark:border-border-hover-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full px-4 py-2 pr-10 bg-surface-raised-light dark:bg-surface-base-dark border border-border-hover-light dark:border-border-hover-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i < passwordStrength
                            ? passwordStrength <= 2
                              ? 'bg-red-500'
                              : passwordStrength <= 4
                                ? 'bg-amber-500'
                                : 'bg-green-500'
                            : 'bg-border-subtle-light dark:bg-border-subtle-dark'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-text-tertiary-light dark:text-text-secondary-dark">
                    {passwordStrength <= 2 && 'Weak password'}
                    {passwordStrength > 2 && passwordStrength <= 4 && 'Medium strength'}
                    {passwordStrength > 4 && 'Strong password'}
                  </p>
                </div>
              )}

              <ul className="mt-2 space-y-1 text-xs text-text-tertiary-light dark:text-text-secondary-dark">
                <li className={newPassword.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}>
                  • At least 8 characters
                </li>
                <li className={/[a-z]/.test(newPassword) ? 'text-green-600 dark:text-green-400' : ''}>
                  • Contains lowercase letter
                </li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-600 dark:text-green-400' : ''}>
                  • Contains uppercase letter
                </li>
                <li className={/[0-9]/.test(newPassword) ? 'text-green-600 dark:text-green-400' : ''}>
                  • Contains number
                </li>
              </ul>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
                Confirm New Password
              </label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-4 py-2 bg-surface-raised-light dark:bg-surface-base-dark border border-border-hover-light dark:border-border-hover-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              />
            </div>

            {passwordError && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                {hasPassword ? 'Password updated' : 'Password set successfully'}
              </div>
            )}

            <button
              type="submit"
              disabled={isChangingPassword}
              className={`${btn.primary.md} flex items-center gap-2`}
            >
              {isChangingPassword ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {hasPassword ? 'Change password' : 'Set password'}
            </button>
          </form>

          {/* ── Connected Accounts / Sign-in Methods section ── */}
          <div className="pt-6 border-t border-border-subtle-light dark:border-border-subtle-dark">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark mb-4">
              Sign-in Methods
            </h3>

            <div className="space-y-2">
              {accounts.map((account) => {
                const isLast = accounts.length === 1;
                const providerLabel =
                  account.provider === 'credential'
                    ? 'Email / Password'
                    : account.provider.charAt(0).toUpperCase() + account.provider.slice(1);

                const remainingLabels = accounts
                  .filter((a) => a.provider !== account.provider)
                  .map((a) =>
                    a.provider === 'credential'
                      ? 'your password'
                      : a.provider.charAt(0).toUpperCase() + a.provider.slice(1),
                  )
                  .join(' or ');

                return (
                  <div
                    key={account.provider}
                    className="flex items-center justify-between py-2 px-3 bg-surface-base-light dark:bg-surface-base-dark rounded-lg border border-border-subtle-light dark:border-border-subtle-dark"
                  >
                    <div>
                      <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                        {providerLabel}
                      </span>
                      <span className="ml-2 text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                        {user?.email}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        if (isLast) return;
                        const confirmMsg = `Remove ${providerLabel} sign-in? You'll still be able to sign in with ${remainingLabels}.`;
                        if (!window.confirm(confirmMsg)) return;
                        try {
                          await disconnectAccount(account.provider);
                          await refetchAccounts();
                        } catch (err) {
                          alert(err instanceof Error ? err.message : 'Failed to disconnect');
                        }
                      }}
                      disabled={isLast}
                      title={isLast ? 'You need at least one sign-in method' : `Remove ${providerLabel}`}
                      className="text-xs text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      Disconnect
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Connect buttons for unlinked providers */}
            <div className="mt-3 space-y-2">
              {(['google', 'github'] as const)
                .filter((p) => !accounts.some((a) => a.provider === p))
                .map((provider) => (
                  <button
                    key={provider}
                    onClick={async () => {
                      try {
                        const url = await linkSocialAccount(provider, `${window.location.origin}/profile`);
                        window.location.href = url;
                      } catch (err) {
                        alert(err instanceof Error ? err.message : `Failed to connect ${provider}`);
                      }
                    }}
                    className={`${btn.ghost.md} w-full flex items-center gap-2`}
                  >
                    + Connect {provider.charAt(0).toUpperCase() + provider.slice(1)}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
  );  // closes content const

  if (onBack) {
    return (
      <div className="h-full overflow-y-auto pb-safe">
        {content}
      </div>
    );
  }
  return content;
}
